import { Injectable, NgZone } from '@angular/core';
import { Subject } from 'rxjs';
import {
  AssistanceRequested,
  AssistanceResolved,
  BayManagerRealtimeClient,
  CommandResult,
  CommandStatusChanged,
  ConnectionState,
  EventChanged,
  ExtensionRequested,
  ExtensionResolved,
  IssueCommandOptions,
  PendingRequest,
  SessionState,
  SimulatorPresence,
  SimulatorStatus,
} from '@fullswing/bay-manager-realtime';
import { environment } from 'src/environments/environment';
import { AuthService } from './AuthService';

@Injectable({
  providedIn: 'root',
})
export class RealtimeService {
  private readonly client: BayManagerRealtimeClient;

  private readonly onlineSimulators = new Set<string>();
  private companyId: number | null = null;

  private readonly simulatorPresence = new Subject<SimulatorPresence>();
  public readonly simulatorPresence$ = this.simulatorPresence.asObservable();

  private readonly commandStatus = new Subject<CommandStatusChanged>();
  public readonly commandStatus$ = this.commandStatus.asObservable();

  private readonly eventChanged = new Subject<EventChanged>();
  public readonly eventChanged$ = this.eventChanged.asObservable();

  private readonly assistanceRequested = new Subject<AssistanceRequested>();
  public readonly assistanceRequested$ = this.assistanceRequested.asObservable();

  private readonly assistanceResolved = new Subject<AssistanceResolved>();
  public readonly assistanceResolved$ = this.assistanceResolved.asObservable();

  private readonly extensionRequested = new Subject<ExtensionRequested>();
  public readonly extensionRequested$ = this.extensionRequested.asObservable();

  private readonly extensionResolved = new Subject<ExtensionResolved>();
  public readonly extensionResolved$ = this.extensionResolved.asObservable();

  private readonly sessionStarted = new Subject<SessionState>();
  public readonly sessionStarted$ = this.sessionStarted.asObservable();

  private readonly sessionEnded = new Subject<SessionState>();
  public readonly sessionEnded$ = this.sessionEnded.asObservable();

  private readonly connectionState = new Subject<ConnectionState>();
  public readonly connectionState$ = this.connectionState.asObservable();

  constructor(
    private ngZone: NgZone,
    private authService: AuthService,
  ) {
    this.client = new BayManagerRealtimeClient({
      baseApiUrl: environment.apiServer,
      getToken: () => this.authService.getIdTokenString(),
    });

    this.client.onSimulatorPresence((payload) => {
      if (payload.isOnline) {
        this.onlineSimulators.add(payload.simulatorIdentifier);
      } else {
        this.onlineSimulators.delete(payload.simulatorIdentifier);
      }
      this.emit(this.simulatorPresence, payload);
    });
    this.client.onCommandStatus((payload) => this.emit(this.commandStatus, payload));
    this.client.onEventChanged((payload) => this.emit(this.eventChanged, payload));
    this.client.onAssistanceRequested((payload) => this.emit(this.assistanceRequested, payload));
    this.client.onAssistanceResolved((payload) => this.emit(this.assistanceResolved, payload));
    this.client.onExtensionRequested((payload) => this.emit(this.extensionRequested, payload));
    this.client.onExtensionResolved((payload) => this.emit(this.extensionResolved, payload));
    this.client.onSessionStarted((payload) => this.emit(this.sessionStarted, payload));
    this.client.onSessionEnded((payload) => this.emit(this.sessionEnded, payload));
    this.client.onConnectionState((state) => this.emit(this.connectionState, state));
  }

  connect(companyId: number): void {
    this.companyId = companyId;
    this.client.connect(companyId);

    void this.refreshOnlineSimulators();
  }

  disconnect(): void {
    this.client.disconnect();
    this.onlineSimulators.clear();
    this.companyId = null;
  }

  lockSimulator(simulatorIdentifier: string, options?: IssueCommandOptions): Promise<CommandResult> {
    return this.client.lockSimulator(simulatorIdentifier, options);
  }

  unlockSimulator(simulatorIdentifier: string, options?: IssueCommandOptions): Promise<CommandResult> {
    return this.client.unlockSimulator(simulatorIdentifier, options);
  }

  sendExtensionDecision(eventId: number, approved: boolean): Promise<void> {
    return this.client.sendExtensionDecision(eventId, approved);
  }

  resolveAssistance(simulatorIdentifier: string): Promise<void> {
    return this.client.resolveAssistance(simulatorIdentifier);
  }

  getOnlineSimulators(companyId: number): Promise<SimulatorStatus[]> {
    return this.client.getOnlineSimulators(companyId);
  }

  getPendingRequests(): Promise<PendingRequest[]> {
    if (this.companyId == null) {
      return Promise.resolve([]);
    }
    return this.client.getPendingRequests(this.companyId);
  }

  async refreshOnlineSimulators(): Promise<string[]> {
    if (this.companyId == null) {
      return [];
    }

    const statuses = await this.client.getOnlineSimulators(this.companyId);
    this.onlineSimulators.clear();
    for (const status of statuses) {
      if (status.isOnline) {
        this.onlineSimulators.add(status.simulatorIdentifier);
      }
    }

    return Array.from(this.onlineSimulators);
  }

  isSimulatorOnline(simulatorIdentifier: string): boolean {
    return this.onlineSimulators.has(simulatorIdentifier);
  }

  private emit<T>(subject: Subject<T>, payload: T): void {
    this.ngZone.run(() => subject.next(payload));
  }
}
